import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  CalculatedPsoGasTariff,
  GetCalculatedPsoGasTariffsParams,
  PsoGasTariff,
  PsoGasTariffRequestBody,
  PsoGasTariffUpdateBody,
} from './types';

export const psoGasTariffsApi = createApi({
  reducerPath: 'psoGasTariffsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/pso-gas-tariffs`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['PsoGasTariff', 'PsoGasTariffs'],
  endpoints: builder => ({
    getPsoGasTariffsI: builder.infiniteQuery<
      PsoGasTariff[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['PsoGasTariff', 'PsoGasTariffs'],
    }),
    getCalculatedPsoGasTariffsI: builder.infiniteQuery<
      CalculatedPsoGasTariff[],
      FiltersGrid & GetCalculatedPsoGasTariffsParams,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/calculated`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['PsoGasTariff', 'PsoGasTariffs'],
    }),
    createPsoGasTariff: builder.mutation<string, PsoGasTariffRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: () => ['PsoGasTariffs'],
    }),
    updatePsoGasTariff: builder.mutation<
      void,
      { id: string } & PsoGasTariffUpdateBody
    >({
      query: ({ id, ...data }) => ({
        url: `/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        'PsoGasTariffs',
        { type: 'PsoGasTariff', id },
      ],
    }),
  }),
});

export const { useCreatePsoGasTariffMutation, useUpdatePsoGasTariffMutation } =
  psoGasTariffsApi;

export const useGetPsoGasTariffsInfiniteQuery =
  normalizeInfiniteQueryData<PsoGasTariff>(
    psoGasTariffsApi.useGetPsoGasTariffsIInfiniteQuery,
  );

export const useGetCalculatedPsoGasTariffsInfiniteQuery =
  normalizeInfiniteQueryData<CalculatedPsoGasTariff>(
    psoGasTariffsApi.useGetCalculatedPsoGasTariffsIInfiniteQuery,
  );
